const TORQUE_TOOL_CONTEXT_KEY = 'tentboxTorqueToolContext'

function normalizeRoofType (value) {
  return (value || '').trim().toLowerCase()
}

function parseRoofTypes (value) {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
  } catch (e) {
    return []
  }
}

function readTorqueToolContext () {
  try {
    const raw = sessionStorage.getItem(TORQUE_TOOL_CONTEXT_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function clearTorqueToolContext () {
  try {
    sessionStorage.removeItem(TORQUE_TOOL_CONTEXT_KEY)
  } catch (e) {}
}

class TorqueToolModal extends HTMLElement {
  constructor () {
    super()
    this.initialize()
  }

  initialize () {
    if (!window.TentBox?.torqueToolModalEnabled) {
      return
    }

    this.config = window.TentBox.torqueToolModal || {}
    this.pendingCartResponse = null
    this.cartDrawerShouldOpen = false
    this.originalRenderContents = null
    this.programmaticClose = false
    this.modalOpen = false
    this.activeTool = null
    this.addedProductTitle = ''
    this.toastTimer = null
    this.toastShowTimer = null

    this.waitForCartDrawerAndIntercept()

    window.addEventListener('cart:updated', this.handleCartUpdated.bind(this))
    window.addEventListener('hide:page-obscure', this.handleModalClosed.bind(this))

    this.previousCartItems = this.getCurrentCartItemsFromResponse(null)
  }

  waitForCartDrawerAndIntercept () {
    const cartDrawer = document.querySelector('cart-drawer')
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      this.interceptCartDrawerRenderContents()
    } else {
      setTimeout(() => this.waitForCartDrawerAndIntercept(), 100)
    }
  }

  interceptCartDrawerRenderContents () {
    const cartDrawer = document.querySelector('cart-drawer')
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return

    this.originalRenderContents = cartDrawer.renderContents.bind(cartDrawer)

    cartDrawer.renderContents = (parsedState) => {
      const currentCartItems = this.getCurrentCartItemsFromResponse(parsedState)
      const newSurtItems = this.getNewSurtItems(this.previousCartItems, currentCartItems)

      if (newSurtItems.length > 0 && !this.cartHasTorqueTool(currentCartItems)) {
        const tool = this.resolveToolForAdd()
        if (tool) {
          this.pendingCartResponse = parsedState
          this.cartDrawerShouldOpen = true
          this.previousCartItems = currentCartItems
          this.activeTool = tool
          this.addedProductTitle = newSurtItems[0]?.title || ''

          this.updateCartUIOnly(parsedState)
          this.showModal()
          clearTorqueToolContext()
          return
        }
      }

      clearTorqueToolContext()
      this.previousCartItems = currentCartItems
      this.originalRenderContents(parsedState)
    }
  }

  resolveToolForAdd () {
    const context = readTorqueToolContext()
    const toolKey = context?.toolKey || this.resolveToolKeyFromContext(context)
    if (!toolKey) return null
    const tool = this.config.tools?.[toolKey] || null
    if (!tool?.variantId) return null
    return tool
  }

  resolveToolKeyFromContext (context) {
    const roofTypes = this.collectRoofTypes(context)
    return TorqueToolModal.resolveToolKey(roofTypes, this.config)
  }

  collectRoofTypes (context) {
    if (context?.roofType) {
      return [context.roofType]
    }
    if (context?.roofTypes?.length) {
      return context.roofTypes
    }
    return []
  }

  static resolveToolKey (roofTypes, config) {
    const normalized = roofTypes.map(normalizeRoofType).filter(Boolean)
    if (!normalized.length) return null

    const gutteredKeys = (config.gutteredTypes || []).map(normalizeRoofType)
    const tKeys = (config.tTypes || []).map(normalizeRoofType)
    const lKeys = (config.lTypes || []).map(normalizeRoofType)

    const withoutGuttered = normalized.filter((type) => !gutteredKeys.includes(type))
    if (!withoutGuttered.length) return null

    const hasL = withoutGuttered.some((type) => lKeys.includes(type))
    const hasT = withoutGuttered.some((type) => tKeys.includes(type))

    if (hasL && !hasT) return 'l'
    if (hasT && !hasL) return 't'
    if (hasL && hasT) return 't'
    return null
  }

  static storeContextFromProductForm (productForm) {
    const handle = productForm.dataset.productHandle || ''
    if (!/-SURT-/i.test(handle)) return

    const roofType = productForm.dataset.roofType
    const roofTypes = parseRoofTypes(productForm.dataset.roofTypes)
    const urlRoofType = new URLSearchParams(window.location.search).get('roof_type')

    let resolvedTypes = []
    if (roofType) {
      resolvedTypes = [roofType]
    } else if (urlRoofType) {
      resolvedTypes = [urlRoofType]
    } else if (roofTypes.length) {
      resolvedTypes = roofTypes
    }

    const config = window.TentBox?.torqueToolModal || {}
    const toolKey = TorqueToolModal.resolveToolKey(resolvedTypes, config)
    if (!toolKey) return

    try {
      sessionStorage.setItem(TORQUE_TOOL_CONTEXT_KEY, JSON.stringify({
        productHandle: handle,
        roofType: roofType || urlRoofType || null,
        roofTypes: resolvedTypes,
        toolKey,
      }))
    } catch (e) {}
  }

  updateCartUIOnly (parsedState) {
    const cartIconSection = parsedState.sections && parsedState.sections['cart-icon-bubble']
    if (cartIconSection) {
      const cartIconElement = document.getElementById('cart-icon-bubble')
      if (cartIconElement) {
        cartIconElement.innerHTML = this.getSectionInnerHTML(cartIconSection, '.shopify-section')
      }
    }

    const cartDrawerSection = parsedState.sections && parsedState.sections['cart-drawer']
    if (cartDrawerSection) {
      const cartDrawerElement = document.getElementById('CartDrawer')
      if (cartDrawerElement) {
        const drawerInner = cartDrawerElement.querySelector('.drawer__inner')
        if (drawerInner) {
          drawerInner.innerHTML = this.getSectionInnerHTML(cartDrawerSection, '.drawer__inner')
        }
      }
    }
  }

  getSectionInnerHTML (html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  handleCartUpdated (event) {
    if (!event.detail) return
    this.previousCartItems = this.getCurrentCartItemsFromResponse(event.detail)
  }

  handleModalClosed () {
    if (!this.modalOpen) {
      return
    }

    this.modalOpen = false
    this.removeToast()

    if (this.programmaticClose) {
      this.programmaticClose = false
      return
    }

    if (this.cartDrawerShouldOpen && this.pendingCartResponse) {
      this.openCartDrawer()
    }

    this.cartDrawerShouldOpen = false
    this.pendingCartResponse = null
    this.activeTool = null
    this.addedProductTitle = ''
  }

  clearToastTimer () {
    if (this.toastShowTimer) {
      clearTimeout(this.toastShowTimer)
      this.toastShowTimer = null
    }
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
      this.toastTimer = null
    }
  }

  openCartDrawer () {
    const cartDrawer = document.querySelector('cart-drawer')
    if (!cartDrawer) return

    this.programmaticClose = true

    setTimeout(() => {
      cartDrawer.classList.remove('is-empty')
      cartDrawer.open()
    }, 500)
  }

  getCurrentCartItemsFromResponse (cartResponse) {
    if (cartResponse && cartResponse.sections && cartResponse.sections['cart-drawer']) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(cartResponse.sections['cart-drawer'], 'text/html')
      const cartItems = []

      const itemElements = doc.querySelectorAll('.cart-drawer__cart-item')
      itemElements.forEach(item => {
        const titleLink = item.querySelector('a.cart-drawer__cart-item-title')
        if (titleLink && titleLink.href) {
          const productHandle = this.getProductHandleFromUrl(titleLink.href)
          if (productHandle) {
            const titleText = item.querySelector('.cart-drawer__cart-item-title-text')
            cartItems.push({ id: productHandle, title: titleText ? titleText.textContent.trim() : '' })
          }
        }
      })

      return cartItems
    }

    if (cartResponse && cartResponse.items) {
      return cartResponse.items.map(item => ({
        id: this.getProductHandleFromUrl(item.url),
        variantId: item.variant_id,
        productId: item.product_id,
        quantity: item.quantity,
        title: item.product_title || item.title || '',
      }))
    }

    return this.getCurrentCartItemsFromDOM()
  }

  getCurrentCartItemsFromDOM () {
    try {
      if (window.cart && window.cart.items) {
        return window.cart.items.map(item => ({
          id: this.getProductHandleFromUrl(item.url),
          variantId: item.variant_id,
          productId: item.product_id,
          quantity: item.quantity,
        }))
      }
      const cartItems = []
      const cartDrawerItems = document.querySelectorAll('.cart-drawer__cart-item')
      cartDrawerItems.forEach(item => {
        const productHandle = this.extractProductHandleFromCartItem(item)
        if (productHandle) {
          cartItems.push({ id: productHandle })
        }
      })
      return cartItems
    } catch (error) {
      console.warn('Could not get cart items:', error)
      return []
    }
  }

  getProductHandleFromUrl (url) {
    if (!url) return null
    const urlParts = url.split('/products/')
    if (urlParts.length > 1) {
      return urlParts[1].split('?')[0].split('/')[0]
    }
    return null
  }

  extractProductHandleFromCartItem (itemElement) {
    const titleLink = itemElement.querySelector('a.cart-drawer__cart-item-title')
    if (titleLink && titleLink.href) {
      return this.getProductHandleFromUrl(titleLink.href)
    }
    return null
  }

  getNewSurtItems (previousItems, currentItems) {
    const previousHandles = previousItems.map(item => item.id).filter(Boolean)
    const newItems = currentItems.filter(item => item.id && !previousHandles.includes(item.id))
    return newItems.filter(item => this.isSurtRoofBar(item.id))
  }

  isSurtRoofBar (productHandle) {
    return /-SURT-/i.test(productHandle || '')
  }

  cartHasTorqueTool (cartItems) {
    const handles = [
      this.config.tools?.t?.handle,
      this.config.tools?.l?.handle,
    ].filter(Boolean)

    if (!handles.length) return false

    return cartItems.some(item => handles.includes(item.id))
  }

  showModal () {
    this.modalOpen = true
    this.displayModal()
  }

  displayModal () {
    const modalContent = this.buildModalContent()
    window.dispatchEvent(new CustomEvent('open:modal', {
      detail: {
        width: 'min(600px, 100svw)',
        padding: 'none',
        headerClassAdd: ['absolute', 'h-20', 'top-0', 'left-0', 'right-0', 'z-20', 'block'],
        headerClassRemove: ['border-b', 'mb-4', 'pb-2', 'grid'],
        closeClassAdd: ['absolute', 'top-4', 'right-4', 'z-20'],
        closeClassRemove: [],
      },
    }))
    setTimeout(() => {
      const modal = document.querySelector('reusable-modal')
      const modalBody = modal?.querySelector('.reusable-modal__body')

      if (modalBody) {
        modalBody.innerHTML = modalContent
        this.attachModalEventListeners(modal)

        // Wait for the modal's open transition (mobile slide-up takes ~400ms) to
        // fully settle before showing the toast, so it doesn't render in the
        // wrong spot and jump into place.
        this.clearToastTimer()
        this.toastShowTimer = setTimeout(() => this.renderToast(modal), 500)
      }
    }, 100)
  }

  renderToast (modal) {
    this.removeToast()
    if (!modal) return

    const toastTemplate = this.config.toastText || ''
    if (!toastTemplate || !this.addedProductTitle) return

    const container = modal.querySelector('.reusable-modal-container')
    const modalBox = modal.querySelector('.reusable-modal')
    if (!container || !modalBox) return

    const toastMessage = toastTemplate.replace(/\[product\]/g, this.addedProductTitle)
    const toast = document.createElement('div')
    toast.className = 'torque-tool-modal__toast pointer-events-none fixed z-10051 left-1/2 flex items-center justify-center gap-2.5 bg-success text-white text-sm md:text-base font-medium rounded-md py-2.5 px-4 w-[calc(100vw-20px)] md:w-fit opacity-0 -translate-x-1/2 -translate-y-2 transition-[opacity,transform] duration-200 ease-out'
    toast.setAttribute('role', 'status')
    toast.setAttribute('aria-live', 'polite')
    toast.innerHTML = `
      <span class="tb-icon torque-tool-modal__toast-icon h-5 w-5 shrink-0 text-white" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="100%" height="100%">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </span>
      <span class="torque-tool-modal__toast-text">${toastMessage}</span>
    `

    container.appendChild(toast)
    this.positionToast(toast, modalBox)

    this.toastResizeHandler = () => this.positionToast(toast, modalBox)
    window.addEventListener('resize', this.toastResizeHandler)

    requestAnimationFrame(() => {
      this.positionToast(toast, modalBox)
      toast.classList.remove('opacity-0', '-translate-y-2')
      toast.classList.add('opacity-100', 'translate-y-0')
    })

    const duration = this.config.toastDuration || 5000
    this.toastTimer = setTimeout(() => {
      toast.classList.remove('opacity-100', 'translate-y-0')
      toast.classList.add('opacity-0', '-translate-y-2')
    }, duration)
  }

  positionToast (toast, modalBox) {
    if (!toast || !modalBox) return
    const rect = modalBox.getBoundingClientRect()
    const toastHeight = toast.offsetHeight || 44
    const top = Math.max(8, rect.top - toastHeight - 12)
    toast.style.top = `${top}px`
  }

  removeToast () {
    this.clearToastTimer()
    if (this.toastResizeHandler) {
      window.removeEventListener('resize', this.toastResizeHandler)
      this.toastResizeHandler = null
    }
    document.querySelectorAll('.torque-tool-modal__toast').forEach((toast) => toast.remove())
  }

  buildModalContent () {
    const settings = this.config
    const tool = this.activeTool || {}
    const hasImage = tool.imageMarkup && tool.imageMarkup.trim() !== ''
    const hasBody = settings.body && settings.body.trim() !== ''
    const textAlignClass = settings.textAlign === 'left' ? 'text-left' : 'text-center'
    const roofTypeLabel = tool.roofTypeLabel || ''
    const bodyHtml = (settings.body || '').replace(/\[\[ROOF_TYPE\]\]/g, roofTypeLabel)

    let mediaElement = ''
    if (hasImage) {
      mediaElement = tool.imageMarkup
    }

    let bodyElement = ''
    if (hasBody) {
      bodyElement = `
        <div class="torque-tool-modal__richtext tb-in-page-content tb-list-fix mt-4 ${textAlignClass}">
          ${bodyHtml}
        </div>
      `
    }

    return `
      <div class="torque-tool-modal__content flex flex-col">
        ${mediaElement}
        <div class="torque-tool-modal__body px-6 py-6 ${textAlignClass}">
          <h2 class="torque-tool-modal__title text-2xl font-semibold font-body normal-case">
            ${settings.title}
          </h2>
          ${bodyElement}
          <div class="torque-tool-modal__actions flex flex-col gap-4 mt-6">
            <button type="button" class="button torque-tool-modal__add-button w-full relative">
              <span class="torque-tool-modal__add-button-text">${settings.addText}</span>
              <span class="torque-tool-modal__add-button-spinner absolute inset-0 hidden items-center justify-center">
                <span class="tb-icon h-5 w-5 animate-spin">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="100%" height="100%">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                </span>
                <span class="sr-only">Adding to cart</span>
              </span>
            </button>
            <button type="button" class="button button-white w-full torque-tool-modal__skip-button inline-flex items-center justify-center">
              <span class="torque-tool-modal__skip-button-text inline-flex items-center">${settings.skipText}</span>
              <span class="tb-icon torque-tool-modal__skip-button-arrow h-4 w-4 ml-1 text-orange">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="100%" height="100%">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    `
  }

  attachModalEventListeners (modal) {
    const addButton = modal.querySelector('.torque-tool-modal__add-button')
    const skipButton = modal.querySelector('.torque-tool-modal__skip-button')

    if (skipButton) {
      skipButton.addEventListener('click', () => {
        this.modalOpen = false
        this.cartDrawerShouldOpen = true
        window.dispatchEvent(new CustomEvent('close:modal'))
      })
    }

    if (addButton) {
      addButton.addEventListener('click', () => {
        this.addToolToCart(addButton)
      })
    }
  }

  async addToolToCart (addButton) {
    const variantId = this.activeTool?.variantId
    if (!variantId || addButton.disabled) return

    addButton.disabled = true
    const spinner = addButton.querySelector('.torque-tool-modal__add-button-spinner')
    const text = addButton.querySelector('.torque-tool-modal__add-button-text')
    if (spinner) spinner.classList.replace('hidden', 'flex')
    if (text) text.classList.add('opacity-0')

    try {
      const cartDrawer = document.querySelector('cart-drawer')
      const sectionIds = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : []

      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sectionIds,
          // eslint-disable-next-line camelcase -- Shopify Cart AJAX API expects this exact key
          sections_url: window.location.pathname,
        }),
      })

      const parsed = await response.json()
      if (parsed.status) {
        throw new Error(parsed.description || 'Add to cart failed')
      }

      let finalResponse = parsed
      if ((!parsed.sections || parsed.sections === null) && sectionIds.length) {
        try {
          const [drawerRes, bubbleRes] = await Promise.all([
            fetch(`${window.location.pathname}?section_id=cart-drawer`),
            fetch(`${window.location.pathname}?section_id=cart-icon-bubble`),
          ])
          const sections = {}
          if (drawerRes.ok) sections['cart-drawer'] = await drawerRes.text()
          if (bubbleRes.ok) sections['cart-icon-bubble'] = await bubbleRes.text()
          if (Object.keys(sections).length) {
            finalResponse = { ...parsed, sections }
          }
        } catch (e) {}
      }

      this.modalOpen = false
      this.cartDrawerShouldOpen = false
      this.programmaticClose = true
      this.removeToast()
      window.dispatchEvent(new CustomEvent('close:modal'))

      if (cartDrawer) {
        cartDrawer.classList.remove('is-empty')
        if (this.originalRenderContents) {
          this.originalRenderContents(finalResponse)
        } else {
          cartDrawer.renderContents(finalResponse)
        }
        cartDrawer.open()
      }

      this.pendingCartResponse = null
      this.activeTool = null
      this.addedProductTitle = ''
      this.previousCartItems = this.getCurrentCartItemsFromResponse(finalResponse)
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: finalResponse }))
    } catch (error) {
      console.warn('Could not add torque tool to cart:', error)
      addButton.disabled = false
      if (spinner) spinner.classList.replace('flex', 'hidden')
      if (text) text.classList.remove('opacity-0')
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('torque-tool-modal')) {
      customElements.define('torque-tool-modal', TorqueToolModal)
    }
  })
} else {
  if (!customElements.get('torque-tool-modal')) {
    customElements.define('torque-tool-modal', TorqueToolModal)
  }
}

window.TentBox = window.TentBox || {}
window.TentBox.storeTorqueToolContext = (productForm) => {
  TorqueToolModal.storeContextFromProductForm(productForm)
}
